import { COMPONENT_TYPE_LIST_OPTIONS, normalizeListComponentType } from '../../functions/publicMetadataPolicy.js';
export { COMPONENT_TYPE_LIST_OPTIONS, normalizeListComponentType };

export const COMPONENT_LIST_PLACEMENTS = [
    { value: 'belowHeader', label: 'Below Header' },
    { value: 'inHeader', label: 'Inside Header' },
];

export const COMPONENT_LIST_ALIGNMENTS = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];

export const COMPONENT_LIST_WIDTHS = [
    { value: 'narrow', label: 'Narrow' },
    { value: 'wide', label: 'Wide' },
    { value: 'spread', label: 'Spread Out' },
];

export const DEFAULT_COMPONENT_LIST_BUTTON_STYLE = {
    backgroundColor: '#ffffff',
    textColor: '#183f43',
    borderColor: '#183f43',
    hoverBackgroundColor: '#183f43',
    hoverTextColor: '#ffffff',
    borderWidth: 2,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    paddingY: 6,
    paddingX: 12,
    gap: 10,
};

export const DEFAULT_COMPONENT_LIST_SETTINGS = {
    ...COMPONENT_TYPE_LIST_OPTIONS.reduce((acc, option) => {
        acc[option.type] = true;
        return acc;
    }, {}),
    placement: 'inHeader',
    alignment: 'center',
    width: 'wide',
    buttonStyle: DEFAULT_COMPONENT_LIST_BUTTON_STYLE,
};

const toCssPx = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed >= 0) return `${parsed}px`;
    return `${fallback}px`;
};

export const buildComponentListButtonStyleVars = (buttonStyle = {}) => {
    const nextStyle = {
        ...DEFAULT_COMPONENT_LIST_BUTTON_STYLE,
        ...(buttonStyle && typeof buttonStyle === 'object' ? buttonStyle : {}),
    };

    return {
        '--component-list-button-gap': toCssPx(nextStyle.gap, DEFAULT_COMPONENT_LIST_BUTTON_STYLE.gap),
        '--component-list-button-bg': nextStyle.backgroundColor || DEFAULT_COMPONENT_LIST_BUTTON_STYLE.backgroundColor,
        '--component-list-button-text': nextStyle.textColor || DEFAULT_COMPONENT_LIST_BUTTON_STYLE.textColor,
        '--component-list-button-border': nextStyle.borderColor || DEFAULT_COMPONENT_LIST_BUTTON_STYLE.borderColor,
        '--component-list-button-hover-bg': nextStyle.hoverBackgroundColor || DEFAULT_COMPONENT_LIST_BUTTON_STYLE.hoverBackgroundColor,
        '--component-list-button-hover-text': nextStyle.hoverTextColor || DEFAULT_COMPONENT_LIST_BUTTON_STYLE.hoverTextColor,
        '--component-list-button-border-width': toCssPx(nextStyle.borderWidth, DEFAULT_COMPONENT_LIST_BUTTON_STYLE.borderWidth),
        '--component-list-button-radius': toCssPx(nextStyle.borderRadius, DEFAULT_COMPONENT_LIST_BUTTON_STYLE.borderRadius),
        '--component-list-button-font-size': toCssPx(nextStyle.fontSize, DEFAULT_COMPONENT_LIST_BUTTON_STYLE.fontSize),
        '--component-list-button-font-weight': Number.parseInt(nextStyle.fontWeight, 10) || DEFAULT_COMPONENT_LIST_BUTTON_STYLE.fontWeight,
        '--component-list-button-padding-y': toCssPx(nextStyle.paddingY, DEFAULT_COMPONENT_LIST_BUTTON_STYLE.paddingY),
        '--component-list-button-padding-x': toCssPx(nextStyle.paddingX, DEFAULT_COMPONENT_LIST_BUTTON_STYLE.paddingX),
    };
};

export const normalizeComponentListSettings = (settings = {}) => {
    const source = settings && typeof settings === 'object' ? settings : {};
    const nextSettings = COMPONENT_TYPE_LIST_OPTIONS.reduce((acc, option) => {
        acc[option.type] = source[option.type] !== false;
        return acc;
    }, {});
    const placement = COMPONENT_LIST_PLACEMENTS.some((option) => option.value === source.placement)
        ? source.placement
        : DEFAULT_COMPONENT_LIST_SETTINGS.placement;
    const alignment = COMPONENT_LIST_ALIGNMENTS.some((option) => option.value === source.alignment)
        ? source.alignment
        : DEFAULT_COMPONENT_LIST_SETTINGS.alignment;
    const width = COMPONENT_LIST_WIDTHS.some((option) => option.value === source.width)
        ? source.width
        : DEFAULT_COMPONENT_LIST_SETTINGS.width;
    const rawButtonStyle = source.buttonStyle && typeof source.buttonStyle === 'object'
        ? source.buttonStyle
        : {};

    return {
        ...nextSettings,
        placement,
        alignment,
        width,
        buttonStyle: {
            ...DEFAULT_COMPONENT_LIST_BUTTON_STYLE,
            ...rawButtonStyle,
        },
    };
};

export const countListableComponents = (components = []) => {
    const counts = COMPONENT_TYPE_LIST_OPTIONS.reduce((acc, option) => {
        acc[option.type] = 0;
        return acc;
    }, {});

    (Array.isArray(components) ? components : []).forEach((comp) => {
        const type = normalizeListComponentType(comp?.type);
        if (type) counts[type] += 1;
    });

    return counts;
};

export const getComponentTypeListOption = (type) => {
    const normalizedType = normalizeListComponentType(type);
    return COMPONENT_TYPE_LIST_OPTIONS.find((option) => option.type === normalizedType) || null;
};

export const getAvailableComponentListOptions = (components = [], settings = {}) => {
    const counts = countListableComponents(components);
    const normalizedSettings = normalizeComponentListSettings(settings);
    return COMPONENT_TYPE_LIST_OPTIONS.map((option) => ({
        ...option,
        count: counts[option.type] || 0,
        enabled: normalizedSettings[option.type] !== false,
    })).filter((option) => option.enabled && (option.showCount === false || option.count >= 2));
};

export const buildComponentTypeListPath = ({ username = '', type = '' }) => {
    const normalizedType = normalizeListComponentType(type);
    const safeUsername = String(username || '').trim();
    if (!safeUsername || !normalizedType) return '#';

    const encodedUsername = encodeURIComponent(safeUsername);
    if (normalizedType === 'posts') return `/${encodedUsername}/list/posts`;
    return `/${encodedUsername}/list/${normalizedType}`;
};
